import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../lib/auth';
import { findProfileByUsername } from '../lib/usernames';
import {
  CurrentThoughtBlock, CurrentlyReadingBlock, ToKnowMeBlock,
  TopTropesBlock, ReadingStatsBlock, ReadingGoalBlock, JournalBlockPublic,
  MyShopBlock, AskMeAboutBlock, SubstackBlock, HotTakeBlock,
  CustomShelfBlock, CustomLinksBlock, EventsBlock,
  ShowcaseBlock, RecommendMeBlock, AmazonStorefrontBlock,
  YouTubeBlock, PodcastBlock,
} from './ProfileBlocks';
import FollowButton from '../components/FollowButton';
import ShareButton from '../components/ShareButton';

// 768, not the 1024 the reader's own profile switches at
// (PHONE_LAYOUT_BELOW, ReaderProfile.jsx). The owner's desktop layout puts a
// 260px block palette beside the three columns and this page does not, so
// here the columns are already 225px or wider from 768 up.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// Every block is readOnly here, for every viewer, the owner included: editing
// happens on /profile (the "Edit" button above), and this page's no-op
// onUpdate means nothing typed here could save anyway.
//
// readOnly, not just a no-op onUpdate, because a no-op stops nothing: most
// blocks write to supabase directly. A signed-in visitor could post the
// owner's showcase piece, services, shelf or storefront to the community under
// their own name, and one tap on the owner's Reading Goal overwrote the
// visitor's own goal (audit 0.1). readOnly removes every owner control:
// pencil, add, share, remove and click-to-edit (0.5). CustomLinksBlock also
// shows the report control in its place, which only means anything on
// somebody else's profile.
function ReadonlyBlock({ block, profile }) {
  const noop  = () => {};
  const props = { profile, onUpdate: noop, blockData: block.data, readOnly: true };
  switch (block.type) {
    case 'current_thought':   return <CurrentThoughtBlock   {...props} />;
    case 'currently_reading': return <CurrentlyReadingBlock {...props} />;
    case 'to_know_me':        return <ToKnowMeBlock         {...props} />;
    case 'top_tropes':        return <TopTropesBlock        {...props} />;
    case 'journal':           return <JournalBlockPublic     profile={profile} />;
    case 'reading_stats':     return <ReadingStatsBlock     profile={profile} onOpenCsvModal={noop} readOnly />;
    case 'reading_goal':      return <ReadingGoalBlock      profile={profile} readOnly />;
    case 'my_shop':           return <MyShopBlock           profile={profile} onUpdate={noop} readOnly />;
    case 'ask_me_about':      return <AskMeAboutBlock       profile={profile} onUpdate={noop} readOnly />;
    case 'substack':          return <SubstackBlock         profile={profile} onUpdate={noop} readOnly />;
    case 'hot_take':          return <HotTakeBlock          profile={profile} onUpdate={noop} readOnly />;
    case 'custom_shelf':      return <CustomShelfBlock      blockData={block.data} profile={profile} onUpdateBlock={noop} readOnly />;
    case 'custom_links':      return <CustomLinksBlock      blockData={block.data} blockId={block.id} profile={profile} onUpdateBlock={noop} readOnly />;
    case 'showcase':          return <ShowcaseBlock         profile={profile} onUpdate={noop} readOnly />;
    case 'recommend_me':      return <RecommendMeBlock      profile={profile} onUpdate={noop} readOnly />;
    case 'amazon_storefront': return <AmazonStorefrontBlock profile={profile} onUpdate={noop} readOnly />;
    case 'youtube':           return <YouTubeBlock          profile={profile} onUpdate={noop} readOnly />;
    case 'podcast':           return <PodcastBlock          profile={profile} onUpdate={noop} readOnly />;
    case 'events':            return <EventsBlock           profile={profile} onUpdate={noop} readOnly />;
    default:                  return null;
  }
}

// The ✉️ pill reads `contact_email` now. It used to read `substack_url` and
// render it as `@{value}`, so a reader who typed their address into what looked
// like an email field had it printed on their public profile as a Substack handle.
const SOCIAL_PLATFORMS = [
  { key: 'instagram_handle', label: 'Instagram', icon: '📷', pillBg: 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)', pillColor: '#fff', prefix: '@' },
  { key: 'tiktok_handle',    label: 'TikTok',    icon: '🎵', pillBg: '#000',     pillColor: '#fff',    prefix: '@' },
  { key: 'twitter_handle',   label: 'Twitter/X', icon: '🐦', pillBg: '#f5f5f4',  pillColor: '#44403c', prefix: '@' },
  { key: 'contact_email',    label: 'Email',     icon: '✉️', pillBg: '#f5f5f4',  pillColor: '#44403c', prefix: ''  },
];

export default function PublicProfile() {
  const { username } = useParams();
  const navigate     = useNavigate();
  const isMobile     = useIsMobile();

  const [profile,     setProfile]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [notFound,    setNotFound]    = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [columns,     setColumns]     = useState([[], [], []]);

  useEffect(() => {
    async function preloadImage(src) {
      if (!src) return;
      return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload  = resolve;
        img.onerror = resolve;
        img.src     = src;
      });
    }
    async function load() {
      setLoading(true);
      document.body.removeAttribute('data-images-ready');
      const data = await findProfileByUsername(username);
      if (!data) { setNotFound(true); setLoading(false); return; }
      setProfile(data);
      const blocks = data.profile_blocks || [];
      const cols   = [[], [], []];
      blocks.forEach(b => { const col = b.column ?? 0; if (col >= 0 && col <= 2) cols[col].push(b); });
      cols.forEach(col => col.sort((a, b) => (a.order || 0) - (b.order || 0)));
      setColumns(cols);
      const user = await getCurrentUser();
      setCurrentUser(user);
      const imagesToPreload = [];
      if (data.avatar_url) imagesToPreload.push(data.avatar_url);
      blocks.forEach(block => {
        if (block.data?.books) block.data.books.forEach(b => b.cover && imagesToPreload.push(b.cover));
        if (block.data?.cover) imagesToPreload.push(block.data.cover);
        if (block.data?.items) block.data.items.forEach(i => i.image && imagesToPreload.push(i.image));
        if (block.data?.lists) block.data.lists.forEach(l => l.image && imagesToPreload.push(l.image));
      });
      await Promise.allSettled(imagesToPreload.map(preloadImage));
      setLoading(false);
      setTimeout(() => { document.body.setAttribute('data-images-ready', 'true'); }, 1000);
    }
    load();
  }, [username]);

  useEffect(() => {
    if (!profile) return;
    const title       = `@${profile.username} on Reanlo`;
    const description = [profile.archetype_label, (profile.interest_tags || []).slice(0, 3).join(', '), 'Find me on Reanlo, the social platform for readers'].filter(Boolean).join(' · ');
    const image = profile.avatar_url || `${window.location.origin}/og-default.png`;
    const url   = `${window.location.origin}/u/${profile.username}`;
    function setMeta(property, content) {
      let el = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(property.startsWith('og:') || property.startsWith('twitter:') ? 'property' : 'name', property); document.head.appendChild(el); }
      el.setAttribute('content', content);
    }
    document.title = title;
    setMeta('og:type', 'profile'); setMeta('og:url', url); setMeta('og:title', title);
    setMeta('og:description', description); setMeta('og:image', image);
    setMeta('og:image:width', '1200'); setMeta('og:image:height', '630'); setMeta('og:site_name', 'Reanlo');
    setMeta('twitter:card', 'summary_large_image'); setMeta('twitter:title', title);
    setMeta('twitter:description', description); setMeta('twitter:image', image);
    return () => { document.title = 'Reanlo'; };
  }, [profile]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--brand-paper)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid var(--brand-sand)', borderTopColor: 'var(--feedback-warning)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Loading profile...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: '100vh', background: 'var(--brand-paper)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', padding: 'var(--space-h3)' }}>
        <span style={{ fontSize: 'var(--font-size-mega)', display: 'block', marginBottom: 'var(--space-lg)' }}>📚</span>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-h4)', color: 'var(--neutral-800)', margin: '0 0 8px' }}>Profile not found</h2>
        <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-500)', margin: '0 0 24px' }}>@{username} doesn't exist on Reanlo yet.</p>
        <button onClick={() => navigate('/')} style={{ padding: '10px 24px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: 'pointer' }}>Go Home</button>
      </div>
    </div>
  );

  const isOwn      = currentUser?.id === profile?.user_id;
  const allBlocks  = columns.flat().sort((a, b) => (a.order || 0) - (b.order || 0));

  // Same treatment as ReaderProfile.jsx: the journal is drawn full width,
  // above the grid, everywhere it appears -- not just on the owner's own
  // editing view. Without this a visitor's view of someone's journal stays
  // squished while the owner's looks right, the same backwards-facing shape
  // as the bug that sank #162 the first time.
  //
  // Per later review: it's a fixture on the public side too, not just
  // something drawn full-width when present. Every profile shows one --
  // synthesise a stand-in when there's no stored row, same as
  // ReaderProfile.jsx. JournalBlockPublic fetches by profile.user_id and
  // returns null itself when there's nothing shared, so this is safe on an
  // otherwise-empty profile: the fixture is attempted, and quietly renders
  // nothing if the reader has never shared an entry.
  const journalBlock = allBlocks.find(b => b.type === 'journal')
    || { id: 'journal-fixture', type: 'journal', column: 0, order: -1 };
  const gridColumns  = columns.map(col => col.filter(b => b.type !== 'journal'));
  const restBlocks   = allBlocks.filter(b => b.id !== journalBlock.id);

  function getSocialHref(p, val) {
    if (p.key === 'contact_email') return `mailto:${val}`;
    return `https://${p.label.toLowerCase().replace('/x', '')}.com/${val}`;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--brand-paper)', fontFamily: 'var(--font-body)' }}>

      {/* ── HEADER ── */}
      <div style={{ background: 'var(--brand-white)', borderBottom: '1px solid var(--brand-sand)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '12px 16px' : '28px 32px 24px' }}>

          {/* ══ MOBILE: centered layout — copied from Reader Profile ══ */}
          {isMobile ? (
            <div style={{ position: 'relative', textAlign: 'center' }}>

              {/* Actions top-right */}
              <div style={{ position: 'absolute', top: 0, right: 0 }}>
                {isOwn ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                    <button onClick={() => navigate('/profile')}
                      style={{ padding: '5px 11px', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', cursor: 'pointer' }}>
                      Edit
                    </button>
                    <ShareButton profile={profile} blocks={allBlocks} />
                  </div>
                ) : (
                  <FollowButton targetUserId={profile.user_id} />
                )}
              </div>

              {/* Avatar — centered, no camera icon */}
              <div style={{ display: 'inline-block', position: 'relative', marginBottom: 'var(--space-10)' }}>
                <div style={{ width: 72, height: 72, borderRadius: 'var(--radius-circle)', overflow: 'hidden', background: 'var(--neutral-100)', border: '2.5px solid var(--brand-gold)' }}>
                  {profile?.avatar_url
                    ? <img src={profile.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h3)', color: 'var(--neutral-300)' }}>📷</div>
                  }
                </div>
              </div>

              {/* Username + location */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-6)' }}>
                <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--neutral-800)', fontFamily: 'var(--font-display)' }}>@{profile?.username}</span>
                {profile?.location
                  ? <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>📍 {profile.location}</span>
                  : <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-300)', fontStyle: 'italic' }}>No location</span>
                }
              </div>

              {/* Archetype pill */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-sm)' }}>
                {profile?.archetype_label ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 12px', background: 'var(--neutral-800)', borderRadius: 'var(--radius-xxl)' }}>
                    <span style={{ fontSize: 'var(--font-size-xs)' }}>{profile.archetype_emoji}</span>
                    <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 600, color: 'var(--brand-paper)' }}>{profile.archetype_label}</span>
                  </div>
                ) : null}
              </div>

              {/* Genre pills */}
              {(profile?.interest_tags || []).length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 7 }}>
                  {(profile.interest_tags || []).map(g => (
                    <span key={g} style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', background: 'var(--brand-cream)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xxs)', fontWeight: 500 }}>
                      {g}
                    </span>
                  ))}
                </div>
              )}

              {/* Social pills */}
              {SOCIAL_PLATFORMS.some(p => profile?.[p.key]) && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, flexWrap: 'wrap' }}>
                  {SOCIAL_PLATFORMS.map(p => {
                    const val = profile?.[p.key];
                    if (!val) return null;
                    return (
                      <a key={p.key} href={getSocialHref(p, val)} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '3px 9px', background: p.pillBg, border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-xxs)', color: p.pillColor, textDecoration: 'none', fontWeight: 500 }}>
                        <span>{p.icon}</span>
                        <span>{p.prefix}{val}</span>
                      </a>
                    );
                  })}
                </div>
              )}

            </div>

          ) : (
            /* ══ DESKTOP: horizontal layout ══ */
            <div style={{ display: 'flex', gap: 'var(--space-xxl)', alignItems: 'flex-start' }}>

              {/* Avatar */}
              <div style={{ width: 88, height: 88, borderRadius: 'var(--radius-circle)', overflow: 'hidden', background: 'var(--neutral-100)', border: '3px solid var(--feedback-warning-bg)', flexShrink: 0 }}>
                {profile?.avatar_url
                  ? <img src={profile.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h2)', color: 'var(--neutral-300)' }}>📷</div>
                }
              </div>

              {/* Identity */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>

                {/* Row 1: username + location + archetype | actions */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-md)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--neutral-800)', fontFamily: 'var(--font-display)' }}>@{profile?.username}</span>
                    {profile?.location
                      ? <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>📍 {profile.location}</span>
                      : <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-300)', fontStyle: 'italic' }}>No location set</span>
                    }
                    {profile?.archetype_label && (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 14px', background: 'var(--neutral-800)', borderRadius: 'var(--radius-xxl)' }}>
                        <span style={{ fontSize: 15 }}>{profile.archetype_emoji}</span>
                        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--brand-paper)' }}>{profile.archetype_label}</span>
                      </div>
                    )}
                  </div>
                  <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}>
                    {!isOwn && <FollowButton targetUserId={profile.user_id} />}
                    {isOwn && (
                      <>
                        <button onClick={() => navigate('/profile')}
                          style={{ padding: '9px 18px', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', cursor: 'pointer' }}>
                          Edit Profile
                        </button>
                        <div style={{ width: 1, height: 32, background: 'var(--brand-sand)' }} />
                        <ShareButton profile={profile} blocks={allBlocks} />
                      </>
                    )}
                  </div>
                </div>

                {/* Row 2: Genre pills */}
                {(profile?.interest_tags || []).length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                    {(profile.interest_tags || []).map(g => (
                      <span key={g} style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 12px', background: 'var(--brand-cream)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>
                        {g}
                      </span>
                    ))}
                  </div>
                )}

                {/* Row 3: Social links */}
                {SOCIAL_PLATFORMS.some(p => profile?.[p.key]) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                    {SOCIAL_PLATFORMS.map(p => {
                      const val = profile?.[p.key];
                      if (!val) return null;
                      return (
                        <a key={p.key} href={getSocialHref(p, val)} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: p.pillBg, border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', color: p.pillColor, textDecoration: 'none', fontWeight: 500 }}>
                          <span>{p.icon}</span>
                          <span>{p.prefix}{val}</span>
                        </a>
                      );
                    })}
                  </div>
                )}

              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── BLOCKS ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '12px 16px 24px' : '24px 32px' }}>
        {/* Fixture, always attempted, on both layouts, before anything else --
            NOT gated on allBlocks.length. The old gate short-circuited to the
            "hasn't set up their profile yet" message before either layout
            branch below ever ran, which meant a profile with zero OTHER
            blocks never showed a shared journal entry either. JournalBlockPublic
            already returns null when the reader has nothing shared, so
            attempting it here is safe on a genuinely empty profile too. */}
        <div style={{ marginBottom: 'var(--space-14)' }}>
          <ReadonlyBlock block={journalBlock} profile={profile} />
        </div>
        {allBlocks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: isMobile ? '40px 20px' : '60px 20px', background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', border: '1.5px dashed var(--brand-sand)' }}>
            <span style={{ fontSize: isMobile ? 36 : 48, display: 'block', marginBottom: 'var(--space-md)' }}>📚</span>
            <p style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>@{profile?.username} hasn't added anything else yet</p>
            <p style={{ fontSize: isMobile ? 12 : 14, color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>Check back soon!</p>
          </div>
        ) : isMobile ? (
          // Same `readonly-blocks` class the desktop grid below has always
          // carried. It used to also hide every `button[style*="right: 36px"]`
          // (EDIT_BTN's inline style) as the only thing keeping pencils from a
          // visitor; the blocks take readOnly now and draw no owner controls
          // at all, so that rule is gone. What is left is a
          // `@media (max-width: 900px)` grid rule, which does nothing here
          // because this list is not a grid.
          <div className={!isOwn ? 'readonly-blocks' : undefined}>
            {restBlocks.map(block => (
              <div key={block.id} style={{ marginBottom: 'var(--space-10)' }}>
                <ReadonlyBlock block={block} profile={profile} />
              </div>
            ))}
          </div>
        ) : (
          <div
            className={!isOwn ? 'readonly-blocks' : undefined}
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-14)', alignItems: 'start' }}
          >
            {gridColumns.map((col, colIdx) => (
              <div key={colIdx}>
                {col.map(block => (
                  <div key={block.id} style={{ marginBottom: 'var(--space-md)' }}>
                    <ReadonlyBlock block={block} profile={profile} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin        { to { transform: rotate(360deg); } }
        @keyframes slideUp     { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes shareDropIn { from { opacity:0; transform:translateY(-6px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
        @media (max-width: 900px) { .readonly-blocks { grid-template-columns: 1fr 1fr !important; } }
        @media print {
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          button, [data-no-print] { display: none !important; }
          @page { margin: 0; }
          body { margin: 0; }
        }
      `}</style>
    </div>
  );
}